import { useCallback } from 'react';

import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  MenuItem,
  MenuItemProps,
} from '@/components/poppers/MenuPopper/MenuItem';
import { useModalState } from '@/hooks/useModalState';
import { useReportError } from '@/hooks/useReportError';
import { useRegenerateAiSuggestionsV2ForTargetMutation } from '@/modules/aiSuggestions/graphql/regenerateAISuggestionsV2ForTarget.generated';
import { useIsEntityWorkflowTrackerInProgress } from '@/modules/workflowTrackers/hooks/useIsEntityWorkflowTrackerInProgress';
import { COLORS } from '@/styles/tokens/colors';
import {
  RegenerateAiSuggestionsV2ForTargetOpts,
  WorkflowTrackerKind,
} from '@/types/schema';
import { getPulidKind, PulidKind } from '@/utils/pulid';

export interface RegenerateAISuggestionsMenuItemProps {
  /** Entity or testamentary-entity id whose suggestions should regenerate. */
  targetId: string;
  householdId: string;
  /** Omit to regenerate every applicable kind for the target. */
  opts?: RegenerateAiSuggestionsV2ForTargetOpts;
  /** Omit to skip the confirmation modal and trigger regen immediately on click. */
  confirmation?: {
    heading: string;
    body: string;
  };
  label?: MenuItemProps['label'];
  muiMenuItemProps?: MenuItemProps['muiMenuItemProps'];
  /** Called after the regeneration mutation returns successfully. */
  onCompleted?: () => void;
}

function useIsRegenerationInProgress(
  targetId: string,
  opts: RegenerateAiSuggestionsV2ForTargetOpts | undefined
): boolean {
  const isTE = getPulidKind(targetId) === PulidKind.TestamentaryEntity;
  const detailsKind = isTE
    ? WorkflowTrackerKind.AiSuggestionsV2SubtrustDetailsGeneration
    : WorkflowTrackerKind.AiSuggestionsV2EntityDetailsGeneration;

  const includeDP = opts?.includeDPSuggestions !== false;
  const includeDetails = opts?.includeDetailsSuggestions !== false;

  const dpInProgress = useIsEntityWorkflowTrackerInProgress(
    includeDP ? targetId : null,
    WorkflowTrackerKind.AiSuggestionsV2DpGeneration
  );
  const detailsInProgress = useIsEntityWorkflowTrackerInProgress(
    includeDetails ? targetId : null,
    detailsKind
  );

  return dpInProgress || detailsInProgress;
}

export function RegenerateAISuggestionsMenuItem({
  targetId,
  householdId,
  opts,
  confirmation,
  label = 'Regenerate AI suggestions',
  muiMenuItemProps,
  onCompleted,
}: RegenerateAISuggestionsMenuItemProps) {
  const [{ isModalOpen }, { setModalIsOpen }] = useModalState();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const [regenerate, { loading: isStartingRegen }] =
    useRegenerateAiSuggestionsV2ForTargetMutation();

  const isRegenerationInProgress = useIsRegenerationInProgress(targetId, opts);

  const handleRegenerate = useCallback(async () => {
    try {
      if (isRegenerationInProgress) {
        showFeedback('AI suggestions are already regenerating.');
        return;
      }

      const { data } = await regenerate({
        variables: {
          input: {
            targetID: targetId,
            householdID: householdId,
            ...(opts && { opts }),
          },
        },
      });

      if (!data?.regenerateAISuggestionsV2ForTarget) {
        throw new Error(
          `regenerateAISuggestionsV2ForTarget returned false for target=${targetId}, household=${householdId}`
        );
      }

      showFeedback('Successfully started regenerating suggestions', {
        variant: 'success',
      });

      onCompleted?.();
    } catch (error) {
      reportError('Error regenerating AI suggestions', error as Error);
      showFeedback('Error regenerating AI suggestions. Please try again.');
    } finally {
      setModalIsOpen(false);
    }
  }, [
    householdId,
    isRegenerationInProgress,
    onCompleted,
    opts,
    regenerate,
    reportError,
    setModalIsOpen,
    showFeedback,
    targetId,
  ]);

  const isDisabled = isStartingRegen || isRegenerationInProgress;

  return (
    <>
      {confirmation && isModalOpen && (
        <ConfirmationModal
          isOpen={isModalOpen}
          heading={confirmation.heading}
          cancelText="Cancel"
          confirmText="Regenerate"
          confirmButtonProps={{
            loading: isStartingRegen,
            disabled: isRegenerationInProgress,
          }}
          cancelButtonProps={{ disabled: isStartingRegen }}
          closeOnOutsideClick={!isStartingRegen}
          onClose={(result) => {
            if (result === 'confirm') {
              void handleRegenerate();
              return;
            }
            setModalIsOpen(false);
          }}
        >
          {confirmation.body}
        </ConfirmationModal>
      )}
      <MenuItem
        icon={<Stars01Icon size={16} color={COLORS.TEAL[600]} />}
        label={label}
        disabled={isDisabled}
        onClick={(event) => {
          if (confirmation) {
            event.stopPropagation();
            setModalIsOpen(true);
            return;
          }
          void handleRegenerate();
        }}
        muiMenuItemProps={muiMenuItemProps}
      />
    </>
  );
}
